import React from "react";
import { useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { State } from "../../../store";
import ConsentGroup from "../../shared/ConsentGroup/ConsentGroup";
import { ConsentItem } from "../../shared/ConsentGroup/types";
import EALink from "../../shared/Link/EALink";
import NewsletterCheckbox from "../inputsForm/NewsletterCheckbox";
import { getEAktywniTermsUrl } from "../../../utils/termsLinks";
import "./ConsentInputs.scss";

interface IProps {
  errors: any;
  control: any;
  setValue: any;
  title?: string;
  newsletter?: boolean;
}

const ConsentInputs: React.FC<IProps> = ({ errors, control, setValue, title = "", newsletter }) => {
  const { t } = useTranslation();
  const configuration = useSelector((state: State) => state.config.configuration);
  const regulaminList = useSelector((state: State) => state.config.configuration?.regulaminList) || [];
  const newsletterActive = useSelector((state: State) => state.config.configuration?.newsLetterActive);
  const userNewsletterAccepted = useSelector((state: State) => state.auth.userData.allowMailing);
  const isB2B = Boolean(useSelector((state: State) => state.auth.userData.b2bType));
  // ConsentInputs potrafi renderować się dwukrotnie w tym samym formularzu (osobno dla
  // "bez faktury" i "z fakturą") — przy przełączeniu jedna instancja się odmontowuje,
  // druga montuje na nowo. Lokalny useState(false) resetowałby wtedy checkbox wizualnie
  // do "odznaczone", mimo że w danych formularza (allowMailing) mogła już zostać zapisana
  // wcześniejsza decyzja usera — realny stan formularza i to co widać na ekranie się
  // rozjeżdżały. useWatch czyta wartość bezpośrednio ze wspólnego stanu formularza
  // (przetrwa odmontowanie), więc obie instancje zawsze pokazują to samo.
  const isNewsletterChecked = Boolean(useWatch({ control, name: "allowMailing", defaultValue: false }));

  const getStoreTermsText = () => {
    const storeTerms = regulaminList.find((item) => item?.type === "StoreTerms");
    if (storeTerms?.active && storeTerms?.file) {
      return (
        <>
          {" "}
          {t("oraz")}{" "}
          <a target="_blank" rel="noreferrer" href={storeTerms.file}>
            {t("regulamin")} {t("sprzedaży")}
          </a>
        </>
      );
    }
    return null;
  };

  const buildConsentItems = (): ConsentItem[] => {
    const items: ConsentItem[] = [
      {
        fieldName: "acceptEAktywni",
        required: true,
        label: (
          <span>
            *{t("akceptuje")}{" "}
            <a target="_blank" rel="noreferrer" href={getEAktywniTermsUrl(configuration)}>
              {t("regulamin")} {t("serwisu")} eAktywni.pl
            </a>
            {getStoreTermsText()}
          </span>
        ),
      },
    ];

    const additionalTerms = regulaminList.filter((item) => item.type === "AdditionalTerms");
    additionalTerms.forEach((item, index) => {
      items.push({
        fieldName: `${item.type}${index}` as `${string}`,
        required: true,
        label: (
          <span>
            <a target="_blank" href={item.file} rel="noreferrer">
              *{item.name}
            </a>
          </span>
        ),
      });
    });

    return items;
  };

  const showNewsletter = newsletter && newsletterActive && !userNewsletterAccepted && !isB2B;

  const handleNewsletterChange = (value: boolean) => {
    setValue("allowMailing", value);
  };

  const handleSelectAll = showNewsletter
    ? (checked: boolean) => {
        setValue("allowMailing", checked);
      }
    : undefined;

  return (
    <div className="consent-inputs__container">
      {title && <h3>{title}</h3>}

      <ConsentGroup
        items={buildConsentItems()}
        control={control}
        errors={errors}
        setValue={setValue}
        onSelectAll={handleSelectAll}
        extraChecksForSelectAll={showNewsletter ? [isNewsletterChecked] : undefined}
      />

      <span className="caption">
        {t(
          "Dane osobowe zbieramy na potrzeby realizacji i zakupu biletów, więcej o tym jak przetwarzamy dane osobowe dowiesz się tutaj:",
        )}{" "}
        {configuration.regulaminList[0] && (
          <EALink to={configuration.regulaminList[0].file} title={t("polityka_prywatnosci")} />
        )}
      </span>

      {showNewsletter && (
        <NewsletterCheckbox checked={isNewsletterChecked} onAllowMailingChange={handleNewsletterChange} />
      )}
    </div>
  );
};

export default ConsentInputs;
